import React, { useEffect, useState } from "react";
import ChartDataLabels from "chartjs-plugin-datalabels";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";
import { Line } from "react-chartjs-2";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  Filler,
  ChartDataLabels
);

function RevenueTrend({ chart_data, yearly_target_data, monthly_target_data }) {
  const [displayType, setDisplayType] = useState("chart");
  const [view, setView] = useState("yearly");

  const [data, setData] = useState({
    labels: [],
    datasets: [],
  });

  const [options, setOptions] = useState({});

useEffect(() => {
  if (!chart_data || chart_data.length === 0) return;

  // =====================================================
  // HELPERS
  // =====================================================

  // Convert:
  // 2026-2027 -> 2026-27
  // 2026-27  -> 2026-27
  const normalizeFY = (fy) => {
    if (!fy) return "";

    const parts = String(fy).split("-");

    if (parts.length === 2) {
      const startYear = parts[0];
      const endYear = parts[1];

      if (endYear.length === 4) {
        return `${startYear}-${endYear.slice(-2)}`;
      }
    }

    return fy;
  };

  // Create standard FY structure
  const createFYStructure = () => ({
    yearly: 0,

    quarterly: {
      Q1: 0,
      Q2: 0,
      Q3: 0,
      Q4: 0,
    },

    monthly: {
      Apr: 0,
      May: 0,
      Jun: 0,
      Jul: 0,
      Aug: 0,
      Sep: 0,
      Oct: 0,
      Nov: 0,
      Dec: 0,
      Jan: 0,
      Feb: 0,
      Mar: 0,
    },

    target: {
      yearly: 0,

      quarterly: {
        Q1: 0,
        Q2: 0,
        Q3: 0,
        Q4: 0,
      },

      monthly: {
        Apr: 0,
        May: 0,
        Jun: 0,
        Jul: 0,
        Aug: 0,
        Sep: 0,
        Oct: 0,
        Nov: 0,
        Dec: 0,
        Jan: 0,
        Feb: 0,
        Mar: 0,
      },
    },
  });

  // =====================================================
  // FY MAP
  // =====================================================

  const fyMap = {};

  // =====================================================
  // ACTUAL REVENUE
  // Source:
  // invoice_has_items
  // =====================================================

  chart_data.forEach((item) => {
    const fy = normalizeFY(item.financial_year);

    const month = item.month_name;

    const quarter = item.quarter;

    const revenue =
      Number(item.total_revenue || 0) / 10000000;

    if (!fyMap[fy]) {
      fyMap[fy] = createFYStructure();
    }

    // Yearly revenue
    fyMap[fy].yearly += revenue;

    // Quarterly revenue
    if (fyMap[fy].quarterly[quarter] !== undefined) {
      fyMap[fy].quarterly[quarter] += revenue;
    }

    // Monthly revenue
    if (fyMap[fy].monthly[month] !== undefined) {
      fyMap[fy].monthly[month] += revenue;
    }
  });

  // =====================================================
  // MONTHLY / QUARTERLY TARGET
  //
  // Source:
  // employeeinfo_has_monthly_revenue_targets
  //
  // Used for:
  // 1. Monthly target
  // 2. Quarterly target
  //
  // NOT used for yearly target
  // =====================================================

  if (
    monthly_target_data &&
    monthly_target_data.length > 0
  ) {
    monthly_target_data.forEach((item) => {
      const targetFY = normalizeFY(item.year);

      const monthNumber = Number(item.month);

      const target =
        Number(item.target || 0) / 10000000;

      // Create FY if it doesn't exist
      if (!fyMap[targetFY]) {
        fyMap[targetFY] = createFYStructure();
      }

      // =================================================
      // MONTH MAP
      // =================================================

      const monthMap = {
        4: "Apr",
        5: "May",
        6: "Jun",
        7: "Jul",
        8: "Aug",
        9: "Sep",
        10: "Oct",
        11: "Nov",
        12: "Dec",
        1: "Jan",
        2: "Feb",
        3: "Mar",
      };

      const monthName = monthMap[monthNumber];

      // =================================================
      // QUARTER MAP
      // =================================================

      let quarter = null;

      if ([4, 5, 6].includes(monthNumber)) {
        quarter = "Q1";
      } else if ([7, 8, 9].includes(monthNumber)) {
        quarter = "Q2";
      } else if ([10, 11, 12].includes(monthNumber)) {
        quarter = "Q3";
      } else if ([1, 2, 3].includes(monthNumber)) {
        quarter = "Q4";
      }

      // =================================================
      // MONTHLY TARGET
      // =================================================

      if (monthName) {
        fyMap[targetFY].target.monthly[monthName] =
          target;
      }

      // =================================================
      // QUARTERLY TARGET
      // =================================================

      if (quarter) {
        fyMap[targetFY].target.quarterly[quarter] +=
          target;
      }
    });
  }

  // =====================================================
  // YEARLY TARGET
  //
  // Source:
  // employeeinfo_has_revenue_targets
  //
  // Used ONLY for yearly view
  // =====================================================

  if (
    yearly_target_data &&
    yearly_target_data.length > 0
  ) {
    yearly_target_data.forEach((item) => {
      const targetFY = normalizeFY(item.year);

      const target =
        Number(item.target || 0) / 10000000;

      // Create FY if it doesn't exist
      if (!fyMap[targetFY]) {
        fyMap[targetFY] = createFYStructure();
      }

      // IMPORTANT:
      // Do NOT sum monthly targets here.
      // Yearly target comes directly from
      // employeeinfo_has_revenue_targets.
      fyMap[targetFY].target.yearly = target;
    });
  }

  console.log("Revenue Trend FY Map:", fyMap);

  // =====================================================
  // CREATE DATASETS
  // =====================================================

  let labels = [];

  let datasets = [];

  // =====================================================
  // YEARLY
  //
  // Revenue:
  // chart_data
  //
  // Target:
  // yearly_target_data
  // =====================================================

  if (view === "yearly") {
    labels = Object.keys(fyMap);

    // -----------------------------
    // Revenue Dataset
    // -----------------------------

    const revenueDataset = {
      label: "Revenue",

      data: labels.map((fy) =>
        Number(
          fyMap[fy].yearly.toFixed(2)
        )
      ),

      borderWidth: 2,

      fill: false,

      tension: 0.4,

      pointRadius: 4,
    };

    // -----------------------------
    // Target Dataset
    // -----------------------------

    const targetDataset = {
      label: "Target",

      data: labels.map((fy) => {
        const target =
          fyMap[fy]?.target?.yearly || 0;

        return target > 0
          ? Number(target.toFixed(2))
          : null;
      }),

      borderWidth: 2,

      borderDash: [6, 6],

      pointRadius: 4,

      fill: false,

      tension: 0,
    };

    // Only show target dataset
    // if at least one yearly target exists
    const hasTarget = targetDataset.data.some(
      (value) => value !== null
    );

    datasets = hasTarget
      ? [revenueDataset, targetDataset]
      : [revenueDataset];
  }

  // =====================================================
  // QUARTERLY
  //
  // Revenue:
  // chart_data
  //
  // Target:
  // monthly_target_data
  // =====================================================

  if (view === "quarterly") {
    labels = [
      "Q1",
      "Q2",
      "Q3",
      "Q4",
    ];

    datasets = Object.keys(fyMap).flatMap(
      (fy) => {
        // -----------------------------
        // Revenue
        // -----------------------------

        const revenueDataset = {
          label: fy,

          data: labels.map((quarter) =>
            Number(
              fyMap[fy].quarterly[
                quarter
              ].toFixed(2)
            )
          ),

          borderWidth: 2,

          fill: false,

          tension: 0.4,

          pointRadius: 4,
        };

        // -----------------------------
        // Check Target
        // -----------------------------

        const hasTarget = labels.some(
          (quarter) =>
            (
              fyMap[fy]?.target?.quarterly[
                quarter
              ] || 0
            ) > 0
        );

        // -----------------------------
        // Target
        // -----------------------------

        if (hasTarget) {
          const targetDataset = {
            label: `${fy}\nTarget`,

            data: labels.map(
              (quarter) =>
                Number(
                  fyMap[fy]
                    .target
                    .quarterly[
                      quarter
                    ]
                    .toFixed(2)
                )
            ),

            borderWidth: 2,

            borderDash: [6, 6],

            pointRadius: 4,

            fill: false,

            tension: 0,
          };

          return [
            revenueDataset,
            targetDataset,
          ];
        }

        return [
          revenueDataset,
        ];
      }
    );
  }

  // =====================================================
  // MONTHLY
  //
  // Revenue:
  // chart_data
  //
  // Target:
  // monthly_target_data
  // =====================================================

  if (view === "monthly") {
    labels = [
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
      "Jan",
      "Feb",
      "Mar",
    ];

    datasets = Object.keys(fyMap).flatMap(
      (fy) => {
        // -----------------------------
        // Revenue
        // -----------------------------

        const revenueDataset = {
          label: fy,

          data: labels.map(
            (month) =>
              Number(
                fyMap[fy]
                  .monthly[month]
                  .toFixed(2)
              )
          ),

          borderWidth: 2,

          fill: false,

          tension: 0.4,

          pointRadius: 4,
        };

        // -----------------------------
        // Check Target
        // -----------------------------

        const hasTarget = labels.some(
          (month) =>
            (
              fyMap[fy]?.target?.monthly[
                month
              ] || 0
            ) > 0
        );

        // -----------------------------
        // Target
        // -----------------------------

        if (hasTarget) {
          const targetDataset = {
            label: `${fy}\nTarget`,

            data: labels.map(
              (month) => {
                const target =
                  fyMap[fy]
                    .target
                    .monthly[
                      month
                    ];

                return target > 0
                  ? Number(
                      target.toFixed(2)
                    )
                  : null;
              }
            ),

            borderWidth: 2,

            borderDash: [6, 6],

            pointRadius: 4,

            fill: false,

            tension: 0,
          };

          return [
            revenueDataset,
            targetDataset,
          ];
        }

        return [
          revenueDataset,
        ];
      }
    );
  }

  // =====================================================
  // SET CHART DATA
  // =====================================================

  setData({
    labels,
    datasets,
  });

  // =====================================================
  // CHART OPTIONS
  // =====================================================

  setOptions({
    responsive: true,
    maintainAspectRatio: false,
    resizeDelay: 100,

    plugins: {
      legend: {
        position: "top",
      },

      tooltip: {
        enabled: true,
        mode: "nearest",
        intersect: true,
        callbacks: {
          label: (context) =>
            `₹ ${Number(
              context.raw
            ).toFixed(2)} Cr`,
        },
      },

      datalabels: {
        display: false,
      },
    },

    scales: {
      y: {
        ticks: {
          callback: (value) =>
            `₹ ${Number(
              value
            ).toFixed(2)} Cr`,
        },
      },
    },
  });
}, [
  chart_data,
  yearly_target_data,
  monthly_target_data,
  view,
]);

  return (
    <div style={{ width: "100%" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: "10px",
          marginBottom: 15,
        }}
      >

        {/* Left Side */}
        <div>
          {["yearly", "quarterly", "monthly"].map((type) => (
            <button
              key={type}
              onClick={() => setView(type)}
              style={{
                padding: "6px 18px",
                marginRight: 8,
                borderRadius: 20,
                border: "none",
                background:
                  view === type ? "#1E63E9" : "#ddd",
                color:
                  view === type ? "#fff" : "#333",
                cursor: "pointer",
                textTransform: "capitalize",
              }}
            >
              {type}
            </button>
          ))}
        </div>

        {/* Right Side */}
        <div>
          {["chart", "table"].map((type) => (
            <button
              key={type}
              onClick={() => setDisplayType(type)}
              style={{
                padding: "6px 18px",
                marginLeft: 8,
                borderRadius: 20,
                border: "none",
                background:
                  displayType === type ? "#198754" : "#ddd",
                color:
                  displayType === type ? "#fff" : "#333",
                cursor: "pointer",
                textTransform: "capitalize",
              }}
            >
              {type}
            </button>
          ))}
        </div>
      </div>
      {/* ================= CHART ================= */}
      {displayType === "chart" ? (
        <div
          style={{
            position: "relative",
            width: "100%",
            height: "260px",
            minWidth: 0,
          }}
        >
          <Line
              data={data}
              options={{
                ...options,
                responsive: true,
                maintainAspectRatio: false,
              }}
              style={{
                width: "100%",
                height: "100%",
              }}
            />
        </div>
      ) : (
        /* ================= TABLE ================= */
        <div className="table-responsive">
          <table className="table table-bordered align-middle revenue-trend">
            <thead>
              <tr>
                <th>
                  {view === "yearly"
                    ? "Financial Year"
                    : view === "quarterly"
                    ? "Quarter"
                    : "Month"}
                </th>

                {data.datasets.map((ds, index) => (
                  <th key={index}>{ds.label}</th>
                ))}
              </tr>
            </thead>

            <tbody>
              {data.labels.map((label, rowIndex) => (
                <tr key={rowIndex}>
                  <td>{label}</td>

                  {data.datasets.map((ds, colIndex) => (
                    <td key={colIndex}>
                      {/* <span> */}
                      ₹{" "}
                      {Number(
                        ds.data[rowIndex] || 0
                      ).toFixed(2)}{" "}
                      Cr
                      {/* </span>
                      <hr style={{ margin: 3px; color: #000; border: 0; border-top: var(--bs-border-width) solid; opacity: 1; }}/>
                      <span>
                      ₹1234
                      Cr
                      </span> */}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default RevenueTrend;